import type { CSSProperties } from "react";
import {
  emptyLineFieldLabel,
  lineFieldLabel,
  resolveLineField,
} from "./lineFields";
import { useLineScope } from "./lineScopeContext";
import { useQuoteDocumentContext } from "./quoteDocumentContext";
import { FIELD_PLACEHOLDER_CHIP_CLASS } from "./quoteFields";
import type { FpCommonProps } from "./types";

interface FpLineFieldProps extends FpCommonProps {
  source: string | null;
}

/**
 * Renders `<fp-line-field source="...">`: the bound line-scope value as inline
 * text that keeps its line breaks, or an image for image-format sources.
 *
 * A line field the quote leaves empty never reaches the customer, matching
 * `fp-field`, but it keeps a placeholder chip in the editor so the cell stays
 * visible and selectable. That is what makes a template's table legible while
 * authoring, where no line carries values at all.
 * @param props - The field source and common fp-* props
 * @returns The resolved value, an image, a placeholder chip, or the empty value
 */
export function FpLineField({
  source,
  className,
  style,
  "data-fp-node": dataFpNode,
}: FpLineFieldProps) {
  const { quote, editable } = useQuoteDocumentContext();
  const scope = useLineScope();
  if (!scope) {
    return (
      <span
        className={`${FIELD_PLACEHOLDER_CHIP_CLASS} font-mono ${className ?? ""}`}
        style={style}
        data-fp-node={dataFpNode}
      >
        ⟨fp-line-field outside a repeater⟩
      </span>
    );
  }
  const resolved = resolveLineField(quote, scope, source);
  if (resolved === null) {
    return (
      <span
        className={`${FIELD_PLACEHOLDER_CHIP_CLASS} font-mono ${className ?? ""}`}
        style={style}
        data-fp-node={dataFpNode}
      >
        {`⟨${source ?? "no source"}⟩`}
      </span>
    );
  }
  if (resolved.format === "image") {
    if (resolved.value === "") {
      return null;
    }
    return (
      <img
        src={resolved.value}
        alt=""
        className={className}
        style={{ ...LINE_IMAGE_STYLE, ...style }}
        data-fp-node={dataFpNode}
      />
    );
  }
  if (editable && isUnsetLineValue(resolved.value)) {
    return (
      <span
        className={`${FIELD_PLACEHOLDER_CHIP_CLASS} ${className ?? ""}`}
        style={style}
        data-fp-node={dataFpNode}
      >
        {`⟨${emptyLineFieldLabel(source ?? "", scope.line.type)}⟩`}
      </span>
    );
  }
  return (
    <span
      className={`whitespace-pre-line ${className ?? ""}`}
      style={style}
      data-fp-node={dataFpNode}
      data-field-title={
        editable && source !== null ? lineFieldLabel(source) : undefined
      }
    >
      {resolved.value}
    </span>
  );
}

/**
 * The bounded box a line image draws in, matching the thumbnail the legacy
 * Modern template renders. A width set on the surrounding cell lands on the
 * cell, never on the image, so without this a large asset renders at its
 * natural size and pushes the table off the page. The square caps at the cell's
 * width so a narrow cell shrinks the thumbnail instead of overflowing. An
 * authored style wins.
 */
const LINE_IMAGE_STYLE: CSSProperties = {
  width: "96px",
  maxWidth: "100%",
  aspectRatio: "1 / 1",
  objectFit: "cover",
  borderRadius: "6px",
};

/**
 * What the money formatters render for an absent amount. Text sources resolve
 * to "" when unset, but `unit-price` and `amount` go through the shared
 * currency formatters, which render this instead.
 */
const EMPTY_MONEY_DISPLAY = "-";

/**
 * Whether a resolved line value means "this line has nothing here", which is
 * what the editor draws a chip for.
 * @param value - The resolved line-field value
 * @returns True when the line carries no value for the source
 */
function isUnsetLineValue(value: string): boolean {
  return value === "" || value === EMPTY_MONEY_DISPLAY;
}
